import { motion } from 'framer-motion';
import themeToken from '~theme-token';
import s from './index.module.less';

export interface BrandLoaderProps {
  /**
   * 指定加载器的大小
   * - 数字：同时设置 width 和 height
   * - 对象：分别设置 width 和 height
   * @default 128
   */
  size?: number | { width: number; height: number };
  /** 全屏居中显示，用于页面级初始化等待 */
  fullscreen?: boolean;
}

function darkenHex(hex: string, percent: number): string {
  const h = hex.replace('#', '');
  const r = Math.round(parseInt(h.substring(0, 2), 16) * (1 - percent / 100));
  const g = Math.round(parseInt(h.substring(2, 4), 16) * (1 - percent / 100));
  const b = Math.round(parseInt(h.substring(4, 6), 16) * (1 - percent / 100));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export const BrandLoader = ({ size = 128, fullscreen }: BrandLoaderProps) => {
  const PRIMARY_COLOR = themeToken.global.colorPrimary;
  const GRADIENT_END_COLOR = darkenHex(PRIMARY_COLOR, 20);

  const width = typeof size === 'number' ? size : size.width;
  const height = typeof size === 'number' ? size : size.height;

  const svg = (
    <svg
      width={width}
      height={height}
      viewBox="0 0 256 256"
      xmlns="http://www.w3.org/2000/svg"
      style={{ overflow: 'visible' }} // 允许光晕溢出
    >
      <defs>
        <linearGradient id="cubeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={PRIMARY_COLOR} />
          <stop offset="100%" stopColor={GRADIENT_END_COLOR} />
        </linearGradient>
        {/* 添加一个发光滤镜 */}
        <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="4" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* 1. 外框：描边生长动画 + 轻微缩放呼吸 */}
      <motion.path
        d="M128 24 L218 76 V180 L128 232 L38 180 V76 L128 24Z"
        fill="none"
        stroke={PRIMARY_COLOR}
        strokeWidth="12" // 稍微调细一点，显得更精致
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{
          pathLength: [0, 1, 1, 0], // 画出来 -> 停顿 -> 消失
          opacity: [0, 1, 1, 0],
          scale: [0.9, 1, 1, 0.9],
        }}
        transition={{
          duration: 3,
          ease: 'easeInOut',
          repeat: Infinity,
          times: [0, 0.4, 0.7, 1], // 控制时间轴节奏
        }}
      />

      {/* 2. 内部立方体：上下悬浮 + 透明度渐变 */}
      <motion.g
        initial={{ y: 20, opacity: 0 }}
        animate={{
          y: [20, 0, 0, 20], // 浮起来 -> 悬停 -> 落下
          opacity: [0, 1, 1, 0],
          scale: [0.8, 1, 1, 0.8],
        }}
        transition={{
          duration: 3,
          ease: 'easeInOut',
          repeat: Infinity,
          times: [0, 0.4, 0.7, 1],
        }}
        style={{ transformOrigin: 'center' }} // 确保从中心缩放
      >
        {/* 主体填充 */}
        <path
          d="M128 60 L188 95 V165 L128 200 L68 165 V95 L128 60Z"
          fill="url(#cubeGrad)"
          filter="url(#glow)" // 加上发光滤镜
        />

        {/* 顶部高光：增加闪烁感 */}
        <motion.path
          d="M128 60 L188 95 L128 130 L68 95 Z"
          fill="white"
          animate={{ opacity: [0.1, 0.5, 0.1] }} // 顶部反光闪烁
          transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
        />
      </motion.g>
    </svg>
  );

  if (fullscreen) {
    return <div className={s.fullscreen}>{svg}</div>;
  }

  return svg;
};
